import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import { LoadingSubscriptions } from "@/pages/settings/components/subscription-management/loading-subscriptions";
import {
  SettingsIntegrationPageWrapper,
  SettingsIntegrationHeader,
  SettingsIntegrationScrollableArea,
  SettingsIntegrationSection,
} from "@/pages/settings/components/settings-integration-layout";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButton,
  MDSButtonVariant,
  MDSButtonSlotName,
  MDSIcon,
  MDSIconKind,
  MDSEditableTitle,
  MDSIconButton,
  MDSIconButtonSize,
  MDSLoadingSpinner,
} from "@/mds";
import { MDSTextColor } from "@/mds/components/text";
import { AccountProfileImage } from "@/components/layout/components/account-profile/account-profile-image";

export const SettingsProfilePage: FC = observer(() => {
  const { store, pageStore } = useAppStore();
  const accountManagementPageStore = pageStore.settingsAccountManagementPage;

  const [editedName, setEditedName] = useState(accountManagementPageStore.currentDisplayName);
  const [showUpdateNotice, setShowUpdateNotice] = useState(false);
  const [profilePhotoPreviewUrl, setProfilePhotoPreviewUrl] = useState<string | null>(null);

  const nameRowRef = useRef<HTMLDivElement>(null);
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const escapePressedRef = useRef(false);

  const hasActiveSubscription = accountManagementPageStore.hasRenewingSubscription;
  const isLoading = accountManagementPageStore.isLoading;
  const isUpdatingProfilePhoto = accountManagementPageStore.isUpdatingProfilePhoto;
  const displayedProfilePhotoUrl =
    profilePhotoPreviewUrl ?? store.account.myAccount.profilePhotoUrl;

  useEffect(() => {
    accountManagementPageStore.refresh();
  }, [accountManagementPageStore]);

  useEffect(() => {
    setEditedName(accountManagementPageStore.currentDisplayName);
  }, [accountManagementPageStore.currentDisplayName]);

  useEffect(() => {
    if (!profilePhotoPreviewUrl) {
      return;
    }

    return () => {
      URL.revokeObjectURL(profilePhotoPreviewUrl);
    };
  }, [profilePhotoPreviewUrl]);

  const handleBlur = async () => {
    if (escapePressedRef.current) {
      escapePressedRef.current = false;
      return;
    }
    const trimmed = editedName.trim();
    if (trimmed.length < 2 || trimmed === accountManagementPageStore.currentDisplayName) {
      setEditedName(accountManagementPageStore.currentDisplayName);
      return;
    }
    await accountManagementPageStore.handleUpdateDisplayName(trimmed);
    setShowUpdateNotice(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
      escapePressedRef.current = true;
      setEditedName(accountManagementPageStore.currentDisplayName);
      e.currentTarget.blur();
    }
  };

  const handleProfilePhotoFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0] ?? null;
    event.currentTarget.value = "";

    if (!file) {
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setProfilePhotoPreviewUrl(nextPreviewUrl);

    const didUpdate = await accountManagementPageStore.handleUpdateProfilePhoto(file);
    setProfilePhotoPreviewUrl(null);

    if (didUpdate) {
      setShowUpdateNotice(true);
    }
  };

  return (
    <SettingsIntegrationPageWrapper>
      <SettingsIntegrationHeader breadcrumb="Account" title="Profile" />
      <SettingsIntegrationScrollableArea>
        <SettingsIntegrationSection>
          <StyledSectionTitle variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            YOUR PROFILE
          </StyledSectionTitle>
          <StyledProfileInfo>
            <StyledProfilePhotoPicker
              type="button"
              aria-label="Change profile photo"
              disabled={isUpdatingProfilePhoto}
              onClick={() => profilePhotoInputRef.current?.click()}
            >
              <AccountProfileImage
                photoUrl={displayedProfilePhotoUrl}
                displayName={accountManagementPageStore.currentDisplayName}
                size={40}
              />
              <StyledProfilePhotoOverlay className="profile-photo-overlay" aria-hidden="true">
                Change
              </StyledProfilePhotoOverlay>
            </StyledProfilePhotoPicker>
            <StyledProfilePhotoInput
              ref={profilePhotoInputRef}
              aria-label="Profile photo upload"
              accept="image/jpeg,image/png,image/webp"
              type="file"
              onChange={handleProfilePhotoFileChange}
            />
            <StyledProfileDetails>
              <StyledNameRow ref={nameRowRef}>
                <StyledEditableTitleWrapper>
                  <MDSEditableTitle
                    variant={MDSTextVariant.ParagraphS}
                    placeholder="Name"
                    value={editedName}
                    onChange={e => setEditedName(e.currentTarget.value)}
                    onBlur={() => void handleBlur()}
                    onKeyDown={handleKeyDown}
                    maxLength={64}
                    readOnly={accountManagementPageStore.isUpdatingDisplayName}
                  />
                </StyledEditableTitleWrapper>
                {accountManagementPageStore.isUpdatingDisplayName ? (
                  <span data-test-id="saving-name-spinner">
                    <MDSLoadingSpinner size={10} />
                  </span>
                ) : (
                  <StyledPencilButton
                    iconKind={MDSIconKind.Pen}
                    size={MDSIconButtonSize.XSmall}
                    aria-label="Edit name"
                    onPress={() =>
                      nameRowRef.current?.querySelector<HTMLInputElement>("input")?.focus()
                    }
                  />
                )}
              </StyledNameRow>
              <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                {accountManagementPageStore.currentAccountEmail}
              </MDSText>
              {showUpdateNotice && (
                <MDSText variant={MDSTextVariant.ParagraphS} color={MDSTextColor.Secondary}>
                  Changes may take up to 24 hours to reflect on your account
                </MDSText>
              )}
            </StyledProfileDetails>
          </StyledProfileInfo>
        </SettingsIntegrationSection>

        <SettingsIntegrationSection>
          <StyledSectionTitle variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
            DANGER ZONE
          </StyledSectionTitle>
          {isLoading ? (
            <LoadingSubscriptions />
          ) : (
            <StyledDangerContent>
              {hasActiveSubscription ? (
                <>
                  <MDSText variant={MDSTextVariant.ParagraphS}>
                    You have an active Mem subscription. Manage your subscription before deleting
                    your account.
                  </MDSText>
                  <MDSButton
                    variant={MDSButtonVariant.Default}
                    onClick={() =>
                      store.settingsModal.open(SettingsTabId.SubscriptionManagementPage)
                    }
                  >
                    Manage subscription
                    <MDSIcon slot={MDSButtonSlotName.RightIcon} kind={MDSIconKind.ArrowUpRight} />
                  </MDSButton>
                </>
              ) : (
                <>
                  <MDSText variant={MDSTextVariant.ParagraphS}>
                    By clicking the button below you will be asked to confirm that you want to
                    delete your account. <Strong>This action is irreversible.</Strong>
                  </MDSText>
                  <MDSButton
                    onClick={() => accountManagementPageStore.openDeleteAccountModal()}
                    variant={MDSButtonVariant.DangerFilled}
                  >
                    <MDSIcon kind={MDSIconKind.Warning} slot={MDSButtonSlotName.LeftIcon} />
                    Continue with account deletion
                  </MDSButton>
                </>
              )}
            </StyledDangerContent>
          )}
        </SettingsIntegrationSection>
      </SettingsIntegrationScrollableArea>
    </SettingsIntegrationPageWrapper>
  );
});

const StyledSectionTitle = styled(MDSText)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
`;

const StyledProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const StyledProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["0.5"]};
`;

const StyledProfilePhotoInput = styled.input`
  display: none;
`;

const StyledProfilePhotoOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgb(0 0 0 / 52%);
  color: white;
  font-size: 9px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  line-height: 1;
  opacity: 0;
  transition: opacity 120ms ease;
  pointer-events: none;
`;

const StyledProfilePhotoPicker = styled.button`
  position: relative;
  display: flex;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  overflow: hidden;

  &:disabled {
    cursor: wait;
  }

  &:not(:disabled):hover,
  &:not(:disabled):focus-visible {
    .profile-photo-overlay {
      opacity: 1;
    }
  }
`;

const StyledNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledEditableTitleWrapper = styled.div`
  && input {
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  }
`;

const StyledPencilButton = styled(MDSIconButton)`
  .mds-btn-icon-wrapper {
    height: 12px;
    width: 12px;

    svg {
      height: 12px;
      width: 12px;
    }
  }

  color: ${({ theme }) => theme.colors.icon.secondary};
  fill: ${({ theme }) => theme.colors.icon.secondary};

  .mds-icon {
    color: ${({ theme }) => theme.colors.icon.secondary};
    fill: ${({ theme }) => theme.colors.icon.secondary};
    font-size: 12px;
    width: 12px;
    height: 12px;
  }
`;

const StyledDangerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const Strong = styled.strong`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;
